import { Star } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { useFavoritableState } from './FavoritableContext';
import { useAddFavorite, useFavorites, useRemoveFavorite } from './api';

/**
 * Star toggle in the TopBar. Hidden when the current page hasn't declared
 * a favoritable name via `useFavoritable`.
 *
 * Click while unfavorited  → POST /favorites { name, path }
 * Click while favorited    → DELETE /favorites/:id
 */
export function FavoriteStar() {
  const location = useLocation();
  const t = useT();
  const { name } = useFavoritableState();
  const { data: favorites = [] } = useFavorites();
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();

  if (!name) return null;

  const path = location.pathname + (location.search || '');
  const existing = favorites.find((f) => f.path === path);
  const isFavorited = Boolean(existing);
  const pending = addFavorite.isPending || removeFavorite.isPending;

  function onClick() {
    if (pending) return;
    if (existing) {
      removeFavorite.mutate(existing.id, {
        onSuccess: () => toast({ tone: 'info', title: t('favorites.removed') }),
        onError: (e: Error) => toast({ tone: 'error', title: e.message }),
      });
    } else {
      addFavorite.mutate(
        { name: name!, path },
        {
          onSuccess: () => toast({ tone: 'success', title: t('favorites.added') }),
          onError: (e: Error) => toast({ tone: 'error', title: e.message }),
        },
      );
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      title={isFavorited ? t('favorites.removeTitle') : t('favorites.addTitle')}
      aria-pressed={isFavorited}
      className={cn(
        'group grid h-8 w-8 place-items-center rounded-md transition-all',
        isFavorited
          ? 'text-[var(--color-accent-ink)] hover:bg-[var(--color-accent-08)]'
          : 'text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-accent-ink)]',
      )}
    >
      <Star
        className={cn(
          'h-[18px] w-[18px] transition-transform',
          isFavorited ? 'scale-110' : 'group-hover:scale-110',
        )}
        fill={isFavorited ? 'currentColor' : 'none'}
        strokeWidth={isFavorited ? 1.5 : 2}
      />
    </button>
  );
}
